import { type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui';
import { GlowOrb } from '@/components/effects/glow-orb';
import { FORM_QUESTIONS } from '../data/questions';
import {
  shuffle,
  shuffleOptions,
  type Answer,
  type ShuffledQuestion,
} from '../utils/shuffle-questions';
import { QuestionCard } from './question-card';
import { ProgressDots } from './progress-dots';
import { useSubmitForm } from '../hooks/use-registration-api';
import { useRegistrationFlow } from '../hooks/use-registration-flow';
import { useFormSubmit } from '../hooks/use-form-submit';
import { viewTransition } from '../hooks/use-step-transition';

type Nav = {
  idx: number;
  ans: (Answer | null)[];
  pick: (a: Answer) => void;
  next: () => void;
  pending: boolean;
};
const LEN = FORM_QUESTIONS.length;

function useQuestionNav(questions: ShuffledQuestion[]): Nav {
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  const [ans, setAns] = useState<(Answer | null)[]>(Array(LEN).fill(null));
  const sf = useSubmitForm();
  const translated = ans.map((a, i) =>
    a ? questions[i]!.originalLetter[a] : null
  );
  const submit = useFormSubmit({
    answers: translated,
    submitForm: sf,
    setSessionToken: useRegistrationFlow().setSessionToken,
    navigate: nav,
  });
  const pick = (a: Answer): void => {
    const n = [...ans];
    n[idx] = a;
    setAns(n);
  };
  const next = (): void => {
    if (idx < LEN - 1) viewTransition(() => setIdx(idx + 1));
    else void submit();
  };
  return { idx, ans, pick, next, pending: sf.isPending };
}

type StepProps = { nav: Nav; questions: ShuffledQuestion[] };

function QuestionStep({ nav, questions }: StepProps): ReactNode {
  return (
    <div style={{ viewTransitionName: 'question-card' }}>
      <QuestionCard
        question={questions[nav.idx]!}
        selected={nav.ans[nav.idx] ?? null}
        onSelect={nav.pick}
        stepper={<ProgressDots total={LEN} current={nav.idx} />}
      />
    </div>
  );
}

export function FormQuestionsPage(): ReactNode {
  const questions = useMemo(
    () => shuffle(FORM_QUESTIONS).map(shuffleOptions),
    []
  );
  const nav = useQuestionNav(questions);
  return (
    <div className="relative flex min-h-dvh items-center justify-center bg-void px-md py-lg sm:px-base sm:py-xl">
      <GlowOrb />
      <div className="relative z-10 w-full max-w-[420px] space-y-md sm:space-y-lg">
        <QuestionStep nav={nav} questions={questions} />
        <Button
          size="lg"
          className="w-full"
          disabled={!nav.ans[nav.idx] || nav.pending}
          onClick={nav.next}
        >
          {nav.idx === LEN - 1 ? 'Finalizar' : 'Siguiente'}
        </Button>
      </div>
    </div>
  );
}
